import React, { useState, useEffect } from 'react'

const MembersTab = ({ members, setMembers, teams, gameId, name, triggerAutoSave }) => {
    //参加者
    const [memberName, setMemberName] = useState("");
    const [memberEmail, setMemberEmail] = useState("");
    const [focusMember, setFocusMember] = useState(null);
    const [memberTeam, setMemberTeam] = useState("");
    const [editingMember, setEditingMember] = useState(null); // 表形式編集用

    // 個人ゲームID生成関数
    const generateMemberCode = () => {
        return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    const generateUniqueMemberCode = (existingCodes) => {
        let code;
        do {
            code = generateMemberCode();
        } while (existingCodes.includes(code));
        return code;
    };

    const generatePersonalGameId = (gameId, memberCode) => {
        return `${gameId}-${memberCode}`;
    };

    //参加者を削除
    const deleteMember = (index) => {
        const member = members[index];

        // 登録済み参加者（実際にゲームに参加登録した参加者）は削除不可
        const isRegistered = member.uid && !member.uid.startsWith('manual_');
        if (isRegistered) {
            alert('登録済みの参加者は削除できません。参加者が既にゲームに参加登録しています。');
            return;
        }

        const result = window.confirm('本当に削除しますか？');
        if(!result) return;

        setFocusMember(null);
        setMembers((prev) => {
            return prev.filter((member, i) => i !== index)
        })
        triggerAutoSave();
    }

    //参加者を手動追加
    const addMember = () => {
        if (!memberName.trim()) {
            alert('参加者名を入力してください。');
            return;
        }

        const existingCodes = members.map(m => m.memberCode).filter(Boolean);
        const newMemberCode = generateUniqueMemberCode(existingCodes); // 常にmemberCodeを生成

        const newMember = {
            name: memberName.trim(),
            email: memberEmail.trim() || `member${Date.now()}@example.com`, // 仮のメールアドレス
            uid: `manual_${Date.now()}_${Math.random().toString(36).substring(7)}`, // 手動追加用のUID
            team: memberTeam !== "" ? parseInt(memberTeam) : null,
            memberCode: newMemberCode,
            registered: true // 手動追加は常に事前登録
        };

        setMembers(prev => [...prev, newMember]);

        triggerAutoSave();

        // フォームをリセット
        setMemberName('');
        setMemberEmail('');
        setMemberTeam('');

        // 新しく追加されたメンバーを選択
        setTimeout(() => {
            setFocusMember(members.length);
        }, 100);
    }

    // 表形式での直接編集を保存
    const saveTableEdit = (index, field, value) => {
        setMembers(prev => {
            const newMembers = [...prev];
            if (field === 'team') {
                newMembers[index] = { ...newMembers[index], team: value !== "" ? Number(value) : null };
            } else {
                newMembers[index] = { ...newMembers[index], [field]: value };
            }
            return newMembers;
        });
        triggerAutoSave();
    };

    // CSVファイルを解析して参加者データをインポートする
    const importMembersCsv = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csv = e.target.result;
                const lines = csv.split('\n').filter(line => line.trim() !== '');
                const newMembers = [];
                const existingCodes = members.map(m => m.memberCode).filter(Boolean);

                lines.forEach((line, index) => {
                    const columns = line.split(',').map(col => col.trim());

                    if (columns.length < 1) {
                        alert(`行 ${index + 1}: データが不足しています（最低1列必要）`);
                        return;
                    }

                    const memberName = columns[0];
                    const memberEmail = columns[1] || `member${Date.now()}_${index}@example.com`;
                    const teamName = columns[2] || '';

                    // バリデーション
                    if (!memberName || memberName.trim() === '') {
                        alert(`行 ${index + 1}: 参加者名が空です`);
                        return;
                    }

                    // チーム名からチームIDを検索
                    let teamId = null;
                    if (teamName && teamName.trim() !== '') {
                        const foundTeam = teams.find(team => team.name === teamName.trim());
                        if (foundTeam) {
                            teamId = teams.indexOf(foundTeam);
                        }
                        // チーム名が指定されているが見つからない場合は警告（エラーではない）
                        if (!foundTeam && teamName.trim() !== '') {
                            console.warn(`行 ${index + 1}: チーム「${teamName}」が見つかりません。チームなしで追加します。`);
                        }
                    }

                    // ユニークなメンバーコードを生成
                    const newMemberCode = generateUniqueMemberCode([...existingCodes, ...newMembers.map(m => m.memberCode)]);
                    existingCodes.push(newMemberCode);

                    newMembers.push({
                        name: memberName.trim(),
                        email: memberEmail.trim(),
                        uid: `manual_${Date.now()}_${Math.random().toString(36).substring(7)}`, // 手動追加用のUID
                        team: teamId,
                        memberCode: newMemberCode,
                        registered: true // CSVインポートは事前登録扱い
                    });
                });

                if (newMembers.length > 0) {
                    setMembers(prev => [...prev, ...newMembers]);
                    triggerAutoSave();
                    alert(`${newMembers.length}人の参加者をインポートしました`);
                }
            } catch (error) {
                alert('CSVファイルの読み込みに失敗しました: ' + error.message);
            }
        };
        reader.readAsText(file);
    };

    // 参加者CSVファイルハンドラー
    const handleMemberCsvImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.csv')) {
            alert('CSVファイルを選択してください');
            return;
        }

        importMembersCsv(file);
        // ファイル入力をリセット
        event.target.value = '';
    };

    // 参加者リストをCSV形式でエクスポートする
    const exportMembersCsv = () => {
        try {
            // CSVヘッダー
            const csvHeader = '個人ゲームID,名前,メールアドレス,チーム名\n';

            // 参加者データをCSV形式に変換
            const csvData = members.map(member => {
                const personalGameId = member.memberCode ? generatePersonalGameId(gameId, member.memberCode) : '';
                const name = member.name || '';
                const email = member.email || '';
                const team = member.team !== null && member.team !== undefined ? teams[Number(member.team)] : null;
                const teamName = team ? team.name : '';

                // CSVフィールドをエスケープ（カンマや改行が含まれる場合はダブルクォートで囲む）
                const escapeField = (field) => {
                    if (field.includes(',') || field.includes('\n') || field.includes('"')) {
                        return '"' + field.replace(/"/g, '""') + '"';
                    }
                    return field;
                };

                return [
                    escapeField(personalGameId),
                    escapeField(name),
                    escapeField(email),
                    escapeField(teamName)
                ].join(',');
            }).join('\n');

            // CSV全体のデータ
            const csvContent = csvHeader + csvData;

            // BOM付きUTF-8でエンコード（Excelで文字化けしないように）
            const bom = '﻿';
            const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

            // ダウンロード用のリンクを作成
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `参加者リスト_${name || 'ゲーム'}_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log(`${members.length}名の参加者データをCSVエクスポートしました`);
            alert('参加者リストをCSVファイルでダウンロードしました');
        } catch (error) {
            console.error('CSV export error:', error);
            alert('CSVファイルの出力でエラーが発生しました');
        }
    };

    //参加者を編集
    useEffect(() => {
        if(focusMember !== null && members[focusMember]) {
            setMemberName(members[focusMember].name);
            setMemberEmail(members[focusMember].email);
            setMemberTeam((members[focusMember].team || members[focusMember].team === 0) ? String(members[focusMember].team) : "");
        } else {
            setMemberName("");
            setMemberEmail("");
            setMemberTeam("");
        }
    }, [focusMember, members, teams]);

    return (
        <div className='mx-3'>
            {/* 参加者管理ヘッダー */}
            <div className='rounded-lg bg-white p-3 mb-3 shadow-sm'>
                <h3 className='text-lg text-gray-800 mb-1'>参加者管理</h3>
                <p className='text-sm ms-2 text-gray-600'>
                    参加者情報を管理し、チームに振り分けてください。<br/>
                    <strong>CSV形式:</strong> 参加者名,メールアドレス(任意),チーム名(完全一致、省略可)
                </p>
                <div className='flex gap-2 mt-3'>
                    <label className='border border-green-500 text-green-500 px-3 py-1 rounded text-sm hover:bg-green-50 cursor-pointer'>
                        <i className='bi bi-file-earmark-arrow-up me-1'></i>CSV インポート
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleMemberCsvImport}
                            className="hidden"
                        />
                    </label>
                    <button
                        onClick={exportMembersCsv}
                        className='border border-blue-500 text-blue-500 px-3 py-1 rounded text-sm hover:bg-blue-50'
                        disabled={!members || members.length === 0}
                    >
                        <i className='bi bi-file-earmark-arrow-down me-1'></i>CSV エクスポート
                    </button>
                </div>
            </div>

            {/* 参加者一覧表 */}
            <div className='rounded-lg bg-white shadow-sm overflow-hidden'>
                <div className='p-3 bg-gray-50 border-b'>
                    <h3 className='text-lg text-gray-800'>参加者一覧 ({members ? members.length : 0}名)</h3>
                </div>

                <div className='overflow-x-auto max-h-96 overflow-y-auto'>
                    <table className='w-full'>
                        <thead className='bg-gray-100 sticky top-0'>
                            <tr>
                                <th className='px-3 py-2 text-left text-sm font-medium text-gray-700 w-32'>参加者ID</th>
                                <th className='px-3 py-2 text-left text-sm font-medium text-gray-700 w-32'>名前</th>
                                <th className='px-3 py-2 text-left text-sm font-medium text-gray-700 w-48'>メールアドレス</th>
                                <th className='px-3 py-2 text-left text-sm font-medium text-gray-700 w-32'>チーム</th>
                                <th className='px-3 py-2 text-left text-sm font-medium text-gray-700 w-24'>事前登録</th>
                                <th className='px-3 py-2 text-center text-sm font-medium text-gray-700 w-20'>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((member, index) => {
                                const isEditing = editingMember === index;
                                const team = member.team !== null && member.team !== undefined ? teams[Number(member.team)] : null;
                                const personalGameId = member.memberCode ? generatePersonalGameId(gameId, member.memberCode) : '-';
                                const isRegistered = member.uid && !member.uid.startsWith('manual_');

                                return (
                                    <tr key={index} className='border-b hover:bg-gray-50'>
                                        {/* 参加者ID */}
                                        <td className='px-3 py-2'>
                                            <div className='text-xs font-mono text-gray-600'>
                                                {personalGameId}
                                                {member.memberCode && (
                                                    <button
                                                        className='ml-1 text-purple-500 hover:text-purple-700'
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(personalGameId);
                                                            alert('個人ゲームIDをコピーしました');
                                                        }}
                                                    >
                                                        <i className='bi bi-copy text-xs'></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>

                                        {/* 名前 */}
                                        <td className='px-3 py-2'>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={member.name}
                                                    onChange={(e) => saveTableEdit(index, 'name', e.target.value)}
                                                    className='w-full p-1 border rounded text-sm focus:outline-purple-300'
                                                />
                                            ) : (
                                                <div className='text-sm'>{member.name}</div>
                                            )}
                                        </td>

                                        {/* メールアドレス */}
                                        <td className='px-3 py-2'>
                                            {isEditing ? (
                                                <input
                                                    type="email"
                                                    value={member.email}
                                                    onChange={(e) => saveTableEdit(index, 'email', e.target.value)}
                                                    className='w-full p-1 border rounded text-sm focus:outline-purple-300'
                                                    disabled={isRegistered}
                                                />
                                            ) : (
                                                <div className='text-sm text-gray-600 truncate' title={member.email}>
                                                    {member.email}
                                                </div>
                                            )}
                                        </td>

                                        {/* チーム */}
                                        <td className='px-3 py-2'>
                                            {isEditing ? (
                                                <select
                                                    value={member.team !== null ? member.team : ""}
                                                    onChange={(e) => saveTableEdit(index, 'team', e.target.value)}
                                                    className='w-full p-1 border rounded text-sm focus:outline-purple-300'
                                                >
                                                    <option value="">未設定</option>
                                                    {teams.map((team, teamIndex) => (
                                                        <option key={teamIndex} value={teamIndex}>{team.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className='text-sm'>
                                                    {team ? team.name : <span className='text-gray-400'>未設定</span>}
                                                </div>
                                            )}
                                        </td>

                                        {/* 事前登録 / 参加済み */}
                                        <td className='px-3 py-2 text-center'>
                                            {isRegistered ? (
                                                <span className='text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full'>
                                                    参加済み
                                                </span>
                                            ) : member.registered ? (
                                                <span className='text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full'>
                                                    事前登録
                                                </span>
                                            ) : (
                                                <span className='text-xs text-gray-400'>
                                                    未登録
                                                </span>
                                            )}
                                        </td>

                                        {/* 操作 */}
                                        <td className='px-3 py-2 text-center'>
                                            <div className='flex justify-center space-x-1'>
                                                {isEditing ? (
                                                    <button
                                                        onClick={() => setEditingMember(null)}
                                                        className='text-green-600 hover:text-green-800 text-sm'
                                                        title="編集完了"
                                                    >
                                                        <i className='bi bi-check-lg'></i>
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => setEditingMember(index)}
                                                        className='text-blue-600 hover:text-blue-800 text-sm'
                                                        title="編集"
                                                    >
                                                        <i className='bi bi-pencil'></i>
                                                    </button>
                                                )}
                                                {/* 削除ボタン - 登録済み参加者は削除不可 */}
                                                {!isRegistered ? (
                                                    <button
                                                        onClick={() => deleteMember(index)}
                                                        className='text-red-600 hover:text-red-800 text-sm'
                                                        title="削除"
                                                    >
                                                        <i className='bi bi-trash'></i>
                                                    </button>
                                                ) : (
                                                    <span
                                                        className='text-gray-400 text-sm cursor-not-allowed'
                                                        title="登録済み参加者は削除できません"
                                                    >
                                                        <i className='bi bi-lock'></i>
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* 新規追加行 */}
                            <tr className='border-b bg-purple-50'>
                                <td className='px-3 py-2 text-center text-gray-400'>-</td>
                                <td className='px-3 py-2'>
                                    <input
                                        type="text"
                                        value={memberName}
                                        onChange={(e) => setMemberName(e.target.value)}
                                        placeholder="新しい参加者名"
                                        className='w-full p-1 border rounded text-sm focus:outline-purple-300'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type="email"
                                        value={memberEmail}
                                        onChange={(e) => setMemberEmail(e.target.value)}
                                        placeholder="メールアドレス（省略可）"
                                        className='w-full p-1 border rounded text-sm focus:outline-purple-300'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <select
                                        value={memberTeam}
                                        onChange={(e) => setMemberTeam(e.target.value)}
                                        className='w-full p-1 border rounded text-sm focus:outline-purple-300'
                                    >
                                        <option value="">チーム選択</option>
                                        {teams.map((team, index) => (
                                            <option key={index} value={index}>{team.name}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className='px-3 py-2 text-center'>
                                    <span className='text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full'>
                                        事前登録
                                    </span>
                                </td>
                                <td className='px-3 py-2 text-center'>
                                    <div className='flex justify-center space-x-1'>
                                        <button
                                            onClick={() => addMember()}
                                            className='text-green-600 hover:text-green-800 text-sm'
                                            title="追加"
                                        >
                                            <i className='bi bi-plus-lg'></i>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setMemberName('');
                                                setMemberEmail('');
                                                setMemberTeam('');
                                            }}
                                            className='text-gray-600 hover:text-gray-800 text-sm'
                                            title="リセット"
                                        >
                                            <i className='bi bi-x-lg'></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default MembersTab
